import { toast } from "sonner";
import api from "@/lib/api";

// File d'attente des devis enregistrés sur l'appareil (hors ligne), un par draft_id
const KEY = "fautras_pending_quotes";
const OLD_KEY = "fautras_pending_quote";
let owner = "";
let inflight = null;

export const setPendingOwner = (id) => { owner = id || ""; };

export const ensureDraftId = () => {
  let id = localStorage.getItem("fautras_draft_id");
  if (!id) { id = crypto.randomUUID(); localStorage.setItem("fautras_draft_id", id); }
  return id;
};

const read = () => {
  let q;
  try { q = JSON.parse(localStorage.getItem(KEY) || "{}") || {}; } catch (e) { q = {}; }
  try {
    const old = JSON.parse(localStorage.getItem(OLD_KEY) || "null");
    if (old?.payload) { q[old.payload.draft_id || "old"] = { ...old, at: Date.now() }; localStorage.removeItem(OLD_KEY); localStorage.setItem(KEY, JSON.stringify(q)); }
  } catch (e) { localStorage.removeItem(OLD_KEY); }
  return q;
};
const write = (q) => {
  localStorage.setItem(KEY, JSON.stringify(q));
  window.dispatchEvent(new Event("pending:change"));
};

export const markPending = (payload, sign = null) => {
  try {
    const q = read();
    const id = payload.draft_id || ensureDraftId();
    // Commande signée hors ligne : son contenu ne change plus
    if (q[id]?.sign && !sign) return;
    q[id] = { owner, payload: { ...payload, draft_id: id }, at: Date.now(), ...(sign ? { sign } : {}) };
    write(q);
  } catch (e) {
    toast.error("Stockage de l'appareil plein : enregistrez le devis manuellement.", { id: "autosave-storage" });
  }
};
const drop = (id) => { const q = read(); if (q[id]) { delete q[id]; write(q); } };
export const clearPending = (id = localStorage.getItem("fautras_draft_id")) => { if (id) drop(id); toast.dismiss("autosave-failed"); };

const label = (p) => [p?.client?.name, p?.model_name].filter(Boolean).join(" · ");

export const pendingList = () => Object.values(read()).filter((e) => !e.owner || !owner || e.owner === owner);
export const hasPending = () => pendingList().length > 0;

export const notifySaveFailed = (payload, retry, extra = {}) => {
  markPending(payload);
  toast.error(`Le devis ${label(payload)} n'a pas pu être enregistré automatiquement`, {
    id: "autosave-failed",
    duration: Infinity,
    description: "Votre saisie est conservée sur cet appareil et sera renvoyée automatiquement. Vous pouvez aussi réessayer maintenant.",
    action: { label: "Réessayer", onClick: () => retry() },
    ...extra,
  });
};

const stage = (id, patch) => { const q = read(); if (q[id]) { q[id] = { ...q[id], ...patch }; write(q); } };

const sendOne = async (id, entry) => {
  try {
    let num = entry.number;
    if (!entry.saved) {
      const { data } = await api.post("/quotes", entry.payload);
      num = data.quote_number;
      if (!entry.sign) {
        drop(id);
        toast.success(`Devis ${num} (${label(entry.payload)}) envoyé au serveur`, { duration: 8000 });
        return true;
      }
      stage(id, { saved: true, number: num });
    }
    if (!entry.signed) {
      try { await api.put(`/quotes/${id}/signature`, entry.sign); } catch (e) {
        const s = e.response?.status;
        if (s && s >= 400 && s < 500 && ![401, 408, 429].includes(s)) {
          drop(id);
          toast.error(`Devis ${num || ""} enregistré mais commande NON signée : ${e.response?.data?.detail || "refusé"}. Faites signer à nouveau depuis Mes devis.`, { duration: 15000 });
          return true;
        }
        throw e;
      }
      stage(id, { signed: true });
    }
    const { sendQuotePdfEmail } = await import("@/lib/pdfLazy");
    try {
      const r = await sendQuotePdfEmail(id);
      drop(id);
      toast.success(`Commande ${num || ""} (${label(entry.payload)}) signée hors ligne : enregistrée et envoyée à ${r.count} destinataire(s)`, { duration: 10000 });
    } catch (e) {
      if (!e.response || [401, 408, 429, 502, 503, 504].includes(e.response.status)) throw e;
      drop(id);
      toast.error(`Commande ${num || ""} enregistrée, mais l'e-mail n'est pas parti : renvoyez-la depuis Mes devis.`, { duration: 12000 });
    }
    return true;
  } catch (e) {
    const s = e.response?.status;
    if (s && s >= 400 && s < 500 && ![401, 408, 429].includes(s)) {
      drop(id);
      toast.error(`Le devis ${label(entry.payload)} n'a pas pu être enregistré : ${e.response?.data?.detail || "refusé"}`, { duration: 10000 });
      return true;
    }
    return false;
  }
};

const run = async () => {
  const q = read();
  for (const [id, entry] of Object.entries(q)) {
    if (!entry?.payload || (entry.owner && owner && entry.owner !== owner)) continue;
    if (!(await sendOne(id, entry))) return;
  }
  toast.dismiss("autosave-failed");
};

export const retryPending = () => {
  inflight = inflight || run().finally(() => { inflight = null; });
  return inflight;
};

// Renvoi automatique dès le retour de la connexion, et toutes les 30 s tant qu'un devis attend
if (typeof window !== "undefined") {
  window.addEventListener("online", () => { if (hasPending()) retryPending(); });
  window.addEventListener("net:back", () => { if (hasPending()) retryPending(); });
  setInterval(() => { if (hasPending() && navigator.onLine !== false) retryPending(); }, 30000);
}
